/**
 * O que o mapa ATENUA quando há filtro. O mapa nunca esconde por filtro:
 * a fábrica continua inteira na tela e o que não passa fica apagado, para o
 * gestor ver onde está o que procurou sem perder o resto de vista.
 *
 * - Ambiente e peça desmembrada: apagados se não passam no filtro.
 * - Pessoa: apagada se o filtro de colaborador não a inclui; com filtro de
 *   ambiente/tarefa (cliente, lote, status…), só fica acesa quem tem um
 *   ambiente ou tarefa que passa.
 * - Bancada/área: apagadas pelos filtros de posto/subsetor.
 * - Sala: apagada quando nada dela passa (nem ambiente, nem peça, nem tarefa,
 *   nem pessoa).
 */
import { SEM_VALOR } from "./dados/filtros";
import { TIPO_AREA } from "./planta";

// Filtros que falam de lugar/pessoa; os demais são "de conteúdo" (ambiente,
// peça ou tarefa). `tipoItem` é de conteúdo de propósito: pedir só as peças
// desmembradas tem de apagar as salas que não têm nenhuma.
const FILTROS_DE_LUGAR_E_PESSOA = new Set(["setores", "subsetores", "postos", "colaboradores"]);

const ativo = (valor) => {
  if (Array.isArray(valor)) return valor.length > 0;
  if (valor && typeof valor === "object") return Boolean(valor.de || valor.ate);
  return Boolean(String(valor || "").trim());
};

function temFiltroDeConteudo(filtros) {
  return Object.entries(filtros || {}).some(([id, valor]) => !FILTROS_DE_LUGAR_E_PESSOA.has(id) && ativo(valor));
}

/**
 * Conjuntos para o desenho. `filtrados` = saída de aplicarFiltros ({demandas,
 * tarefas, pecas}); `ativos` = há algum filtro em uso. Sem filtro, tudo null
 * (nada apagado).
 * → {passaAmbiente: Set<chaveLinha>|null, passaTarefa: Set<id>|null,
 *    passaPeca: Set<id>|null, pessoaApagada(situacao), bancadaApagada(bancada),
 *    areaApagada(area), salaApagada(setorId, {pessoas, bancadas, areas})}
 */
export function montarDestaque({ filtros, filtrados, ativos }) {
  if (!ativos) {
    const nunca = () => false;
    return {
      passaAmbiente: null,
      passaTarefa: null,
      passaPeca: null,
      pessoaApagada: nunca,
      bancadaApagada: nunca,
      areaApagada: nunca,
      salaApagada: nunca,
    };
  }
  const passaAmbiente = new Set(filtrados.demandas.map((d) => d.chaveLinha));
  const passaTarefa = new Set(filtrados.tarefas.map((t) => t.id));
  const passaPeca = new Set((filtrados.pecas || []).map((p) => p.id));
  const colaboradores = filtros.colaboradores || [];
  const postos = filtros.postos || [];
  const subsetores = filtros.subsetores || [];
  const conteudo = temFiltroDeConteudo(filtros);

  const pessoaApagada = (situacao) => {
    if (!situacao) return true;
    if (colaboradores.length && !colaboradores.includes(situacao.colaborador.id)) return true;
    if (!conteudo) return false;
    const temAmbiente = situacao.ambientes.some((d) => passaAmbiente.has(d.chaveLinha));
    const temTarefa = situacao.tarefasAbertas.some((t) => passaTarefa.has(t.id));
    return !temAmbiente && !temTarefa;
  };
  const bancadaApagada = (bancada) =>
    postos.length > 0 && !postos.includes(bancada.virtual ? SEM_VALOR : bancada.id);
  const areaApagada = (area) =>
    subsetores.length > 0 && !subsetores.includes(area.tipo === TIPO_AREA.SUBSETOR ? area.subsetorId : SEM_VALOR);

  const setores = filtros.setores || [];
  const tarefasPorSetor = new Map();
  filtrados.tarefas.forEach((t) => tarefasPorSetor.set(t.setor_id, (tarefasPorSetor.get(t.setor_id) || 0) + 1));
  // A sala fica acesa se alguma parte dela passa: ambiente, peça, tarefa ou,
  // nos filtros de lugar/pessoa, uma pessoa, bancada ou área escolhida.
  const salaApagada = (setorId, { pessoas = [], bancadas = [], areas = [] } = {}) => {
    if (setores.length && !setores.includes(setorId)) return true;
    if (filtrados.demandas.some((d) => d.setorId === setorId)) return false;
    if ((filtrados.pecas || []).some((p) => p.setorId === setorId)) return false;
    if (tarefasPorSetor.get(setorId)) return false;
    if (conteudo) return true;
    if (colaboradores.length && pessoas.some((s) => !pessoaApagada(s))) return false;
    if (postos.length && bancadas.some((b) => !bancadaApagada(b))) return false;
    if (subsetores.length && areas.some((a) => !areaApagada(a))) return false;
    return colaboradores.length > 0 || postos.length > 0 || subsetores.length > 0;
  };
  return { passaAmbiente, passaTarefa, passaPeca, pessoaApagada, bancadaApagada, areaApagada, salaApagada };
}
